import type {ComponentType} from 'react';

import {Conversation} from '@stripe-internal/conversations-client';
import {Visibility} from '@stripe-internal/support-conversations-controller';

import {LiveChatTopBar} from './LiveChatTopBar';
import {RACTopBar} from './RACTopBar/RACTopBar';
import {useConversationMessageSounds} from '../hooks/useConversationMessageSounds';
import {
  useCurrentConversation,
  useVisibility,
} from '../../../core/hooks/appHooks';
import {useLiveChannelStatus} from '../hooks/useLiveChannelStatus';
import {useUnseenConversationMessages} from '../hooks/useUnseenConversationMessages';

type TopBarComponentProps = {
  conversation: Conversation;
};

export function ConversationTopBar({
  conversation,
  visibility,
  LiveChatTopBarComponent = LiveChatTopBar,
  RACTopBarComponent = RACTopBar,
}: {
  conversation: Conversation;
  visibility: Visibility;
  LiveChatTopBarComponent?: ComponentType<TopBarComponentProps>;
  RACTopBarComponent?: ComponentType<TopBarComponentProps>;
}) {
  const {isLiveChatActive, isRACActive} = useLiveChannelStatus(conversation);

  if (visibility !== Visibility.Open) {
    return null;
  }

  if (isLiveChatActive) {
    return <LiveChatTopBarComponent conversation={conversation} />;
  } else if (isRACActive) {
    return <RACTopBarComponent conversation={conversation} />;
  } else {
    return null;
  }
}

function ActiveConversationTopBar({
  conversation,
}: {
  conversation: Conversation;
}) {
  const {visibility} = useVisibility();

  useUnseenConversationMessages(conversation);
  useConversationMessageSounds(conversation);

  return (
    <ConversationTopBar conversation={conversation} visibility={visibility} />
  );
}

export function ActiveTopBar() {
  const currentConversation = useCurrentConversation();

  if (!currentConversation) {
    return null;
  }

  return <ActiveConversationTopBar conversation={currentConversation} />;
}
